import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, shortDate, showMoney } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { SyncStatus } from "@/components/SyncStatus";
import { CustomerSheet } from "@/components/CustomerSheet";
import { CHANNELS } from "@/components/StoreArea";
import { CustomerGeo } from "@/components/CustomerGeo";
import { BestCustomers } from "@/components/BestCustomers";
import { AdviceStats } from "@/components/advisor/AdviceStats";
import { LapsedCustomers } from "@/components/LapsedCustomers";
import { DuplicateCustomers } from "@/components/DuplicateCustomers";
import { CustomerSegments, SegmentBadge, SeasonFilter, NewSeasonFilter, TIERS, RECENCY } from "@/components/CustomerSegments";
import { SALE_PROFILES } from "@/components/SaleCustomers";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Search, Loader2, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Ruler } from "lucide-react";
import { downloadFile } from "@/lib/download";
import { toast } from "sonner";

const Kpi = ({ label, value, sub, testid }) => (
  <Card className="p-5">
    <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-2 font-display text-2xl font-bold" data-testid={testid}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </Card>
);

const SortHead = ({ label, col, sort, onSort, right }) => {
  const active = sort.by === col;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={right ? "text-right" : ""}>
      <button onClick={() => onSort(col)} data-testid={`customers-sort-${col}`} className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : ""}`}>
        {label}<Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
};

const useCustomersSync = () => {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ["sync-status"], queryFn: async () => (await api.get("/shopify/sync-status")).data,
    refetchInterval: (q) => (q.state.data?.customers?.running ? 3000 : 20000),
  });
  const sync = useMutation({
    mutationFn: async () => (await api.post("/customers/sync", null, { params: { full: true } })).data,
    onSuccess: () => { toast.success("Sincronizzazione clienti avviata…"); setTimeout(() => qc.invalidateQueries({ queryKey: ["sync-status"] }), 1000); },
  });
  const running = !!st?.customers?.running;
  useEffect(() => { if (!running) qc.invalidateQueries({ queryKey: ["customers"] }); }, [running, qc]);
  return { sync, running, state: st?.customers };
};

export default function Clienti() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "spent", dir: "desc" });
  const [selected, setSelected] = useState(null);
  const [channel, setChannel] = useState(() => new URLSearchParams(window.location.search).get("channel") || "tutti");
  const [tier, setTier] = useState(null);
  const [recency, setRecency] = useState(null);
  const [seasonStatus, setSeasonStatus] = useState("tutti");
  const [newSeason, setNewSeason] = useState(() => new URLSearchParams(window.location.search).get("new_season") || "tutti");
  const [saleProfile, setSaleProfile] = useState(() => new URLSearchParams(window.location.search).get("sale_profile") || "tutti");
  const [measures, setMeasures] = useState(false);
  const [bestSeason, setBestSeason] = useState(null);
  const [zone, setZone] = useState(null);
  const pickZone = (z) => { setZone(z); setPage(1); setTimeout(() => document.getElementById("customers-list")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const { sync, running, state } = useCustomersSync();
  useEffect(() => { const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    // Apertura dalla lista ordini: /clienti?cliente=<id Shopify o email>
    const ref = new URLSearchParams(window.location.search).get("cliente");
    if (ref) {
      api.get(`/customers/${encodeURIComponent(ref)}`).then((r) => setSelected(r.data.id)).catch(() => toast.error("Cliente non trovato"));
    }
  }, []);
  const toggleSort = (by) => { setSort((s) => (s.by === by ? { by, dir: s.dir === "asc" ? "desc" : "asc" } : { by, dir: by === "name" ? "asc" : "desc" })); setPage(1); };

  const { data: stats } = useQuery({ queryKey: ["customers", "stats"], queryFn: async () => (await api.get("/customers/stats")).data });
  const { data: res, isLoading, isFetching } = useQuery({
    queryKey: ["customers", q, page, sort, channel, tier, recency, seasonStatus, newSeason, saleProfile, measures, zone],
    queryFn: async () => (await api.get("/customers", { params: { q: q || undefined, page, sort: sort.by, direction: sort.dir, channel, tier: tier || undefined, recency: recency || undefined, season_status: seasonStatus, new_season: newSeason, sale_profile: saleProfile, measures: measures ? "con" : undefined, zone_type: zone?.type, zone: zone?.name } })).data,
    placeholderData: keepPreviousData,
  });
  const rows = res?.items || [];
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    const p = new URLSearchParams(Object.entries({ q, sort: sort.by, direction: sort.dir, channel, tier, recency, season_status: seasonStatus, new_season: newSeason, sale_profile: saleProfile, measures: measures ? "con" : undefined }).filter(([, v]) => v));
    try { await downloadFile(`/customers/export.xlsx?${p}`, "clienti.xlsx"); } catch { toast.error("Esportazione non riuscita"); }
    setExporting(false);
  };

  return (
    <div>
      <PageHeader title="Clienti" subtitle="Anagrafica e storico acquisti dei clienti Shopify.">
        <div className="flex flex-col items-end gap-2">
          <Button onClick={() => sync.mutate()} disabled={sync.isPending || running} data-testid="sync-customers-btn">
            {(sync.isPending || running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Sincronizza clienti
          </Button>
          <SyncStatus state={state} testid="sync-status-customers" />
        </div>
      </PageHeader>

      {stats && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Clienti totali" value={num(stats.total)} sub={`${num(stats.buyers)} con almeno un ordine`} testid="customers-total" />
            <Kpi label="Clienti di ritorno" value={num(stats.returning)} sub="2 o più ordini" />
            <Kpi label="Tasso di riacquisto" value={`${num(stats.repeat_rate)}%`} sub="sui clienti che hanno ordinato" testid="customers-repeat-rate" />
            <Kpi label="Nuovi questo mese" value={num(stats.new_this_month)} />
          </div>
          <div className="mb-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
            <BestCustomers season={bestSeason} onSeason={setBestSeason} onSelect={setSelected} />
            <CustomerGeo stats={stats} onZone={pickZone} />
          </div>
          <AdviceStats />
          <LapsedCustomers season={bestSeason} onSelect={setSelected} />
          <DuplicateCustomers onSelect={setSelected} />
          {stats.segments && <SeasonFilter season={stats.segments.season} value={seasonStatus} onChange={(v) => { setSeasonStatus(v); setPage(1); }} />}
          {stats.segments && <NewSeasonFilter season={stats.segments.season} value={newSeason} onChange={(v) => { setNewSeason(v); setPage(1); }} />}
          <div className="-mt-2 mb-4 flex flex-wrap items-center gap-2" data-testid="sale-profile-filter">
            <span className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Profilo saldi</span>
            {[["tutti", "Tutti"], ...Object.entries(SALE_PROFILES).map(([k, v]) => [k, v.label])].map(([k, l]) => (
              <button key={k} onClick={() => { setSaleProfile(k); setPage(1); }} data-testid={`sale-profile-filter-${k}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${saleProfile === k ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>{l}</button>
            ))}
            <button onClick={() => { setMeasures(!measures); setPage(1); }} data-testid="measures-filter"
              className={`ml-2 flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${measures ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>
              <Ruler className="h-3 w-3" />Con misure
            </button>
          </div>
          {stats.segments && <CustomerSegments matrix={stats.segments[seasonStatus]} season={stats.segments.season} tier={tier} recency={recency}
            onTier={(v) => { setTier(v); setPage(1); }} onRecency={(v) => { setRecency(v); setPage(1); }} />}
        </>
      )}

      <div id="customers-list" className="mb-4 flex scroll-mt-4 flex-wrap items-center gap-2">
      <div className="relative w-full sm:w-[480px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Cerca nome, email, telefono, città..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customer-search" />
      </div>
        {[["tutti", "Tutti"], ...Object.entries(CHANNELS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <Button key={k} size="sm" variant={channel === k ? "default" : "outline"} onClick={() => { setChannel(k); setPage(1); }} data-testid={`channel-filter-${k}`}>{l}</Button>
        ))}
        {(tier || recency) && (
          <Button size="sm" variant="ghost" onClick={() => { setTier(null); setRecency(null); setPage(1); }} data-testid="segments-clear">
            Rimuovi filtri: {[tier && TIERS[tier].label, recency && RECENCY[recency].label].filter(Boolean).join(" + ")} ✕
          </Button>
        )}
        {zone && (
          <Button size="sm" variant="secondary" onClick={() => { setZone(null); setPage(1); }} data-testid="zone-filter-clear">
            Zona: {zone.name} ✕
          </Button>
        )}
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportXlsx} disabled={exporting} data-testid="export-customers-btn">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Esporta contatti{res ? ` (${num(res.total)})` : ""}
        </Button>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortHead label="Cliente" col="name" sort={sort} onSort={toggleSort} />
                <TableHead>Contatti</TableHead>
                <TableHead>Città</TableHead>
                <SortHead label="Ultimo acquisto" col="last" sort={sort} onSort={toggleSort} />
                <SortHead label="Ordini" col="orders" sort={sort} onSort={toggleSort} />
                {showMoney() && <SortHead label="Speso" col="spent" sort={sort} onSort={toggleSort} right />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelected(c.id)} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">
                    {c.name}{CHANNELS[c.channel] && <Badge variant="outline" className={`ml-2 text-[10px] ${CHANNELS[c.channel].cls}`}>{CHANNELS[c.channel].label}</Badge>}
                    <span className="ml-2 inline-flex gap-1"><SegmentBadge map={TIERS} value={c.tier} testid={`customer-tier-${c.id}`} />
                      {["solo_saldi", "soprattutto_saldi"].includes(c.sale_profile) && <SegmentBadge map={SALE_PROFILES} value={c.sale_profile} testid={`customer-sale-${c.id}`} />}
                      {c.measurements?.updated_at && <Ruler className="h-3.5 w-3.5 text-primary" title="Misure inserite" data-testid={`customer-measures-${c.id}`} />}
                      {c.season_buyer && <Badge variant="outline" className="border-primary/50 text-[10px] text-primary" data-testid={`customer-season-${c.id}`}>{stats?.segments?.season || "A26"}</Badge>}</span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.email}{c.phone ? <span className="block">{c.phone}</span> : null}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{[c.city, c.province].filter(Boolean).join(" · ") || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <span className="mr-2">{c.last_order_date ? shortDate(c.last_order_date) : "-"}</span>
                    <SegmentBadge map={RECENCY} value={c.recency} testid={`customer-recency-${c.id}`} />
                  </TableCell>
                  <TableCell className="text-sm">{num(c.orders_count)}</TableCell>
                  {showMoney() && <TableCell className="text-right font-semibold">
                    {eur(c.total_spent)}
                    <span className="block text-[11px] font-normal text-muted-foreground" data-testid={`customer-year-spent-${c.id}`}>{res?.season}: {eur(c.season_spent || 0)}</span>
                  </TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Nessun cliente trovato. Premi "Sincronizza clienti".</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      {res && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground" data-testid="customers-pagination">
          <span data-testid="customers-count">{num(res.total)} clienti · pagina {res.page} di {res.pages}{isFetching && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" />}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}>Prima</Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="customers-prev"><ChevronLeft className="h-4 w-4" />Precedente</Button>
            <Button variant="outline" size="sm" disabled={page >= res.pages} onClick={() => setPage((p) => p + 1)} data-testid="customers-next">Successiva<ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <CustomerSheet id={selected} onClose={() => setSelected(null)} onSelect={setSelected} />
    </div>
  );
}
